import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../../utils/api'
import PasswordInput from '../../components/common/PasswordInput'
import PasswordStrengthMeter, { isPasswordValid } from '../../components/common/PasswordStrengthMeter'
import { toast } from '../../components/toast/CustomToast'
import logo from '../../assets/logo.png'

/**
 * Create Password Page - First Access
 * User receives a token via email and creates their initial password
 */

const pageWrapper = (children) => (
  <div className="h-screen overflow-hidden flex items-center justify-center p-4 relative" style={{ backgroundColor: '#0A1C28' }}>
    <div className="absolute top-0 -left-40 w-96 h-96 rounded-full filter blur-3xl opacity-10 animate-pulse" style={{ backgroundColor: '#25C6A4' }}></div>
    <div className="absolute bottom-0 -right-40 w-96 h-96 rounded-full filter blur-3xl opacity-10 animate-pulse" style={{ backgroundColor: '#25C6A4', animationDelay: '2s' }}></div>
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 rounded-full filter blur-3xl opacity-5 animate-pulse" style={{ backgroundColor: '#25C6A4', animationDelay: '4s' }}></div>
    <div className="relative z-10 w-full max-w-md">{children}</div>
  </div>
)

const PageHeader = () => (
  <div className="text-center mb-8">
    <div className="flex justify-center mb-4">
      <div className="logo-spin-border inline-flex items-center justify-center shrink-0" style={{}}>
        <img src={logo} alt="app" className="w-20 h-20 object-contain rounded-full" />
      </div>
    </div>
    <h1 className="text-3xl" style={{ fontFamily: 'Blare, sans-serif', color: '#FFFFFF' }}>
      Solid<span style={{ color: '#25C6A4' }}>.</span>Flow
    </h1>
    <p className="text-sm mt-1" style={{ fontFamily: "'Stolzl', sans-serif", color: 'rgba(255,255,255,0.6)' }}>
      Hub de Integrações
    </p>
  </div>
)

const CreatePasswordPage = () => {
  const navigate = useNavigate()
  const [token, setToken] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [userInfo, setUserInfo] = useState(null)
  const [loadingInfo, setLoadingInfo] = useState(true)

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const tokenParam = urlParams.get('token')

    if (!tokenParam) {
      setError('Token não fornecido. Link inválido ou expirado.')
      setLoadingInfo(false)
      return
    }

    setToken(tokenParam)

    const fetchUserInfo = async () => {
      try {
        const response = await api.get(`/company-users/first-access-info/${tokenParam}`)
        setUserInfo(response.data)
        setLoadingInfo(false)
      } catch (err) {
        const errorMsg = err.response?.data?.detail || 'Link inválido ou expirado'
        setError(typeof errorMsg === 'string' ? errorMsg : 'Erro ao carregar informações')
        setLoadingInfo(false)
      }
    }

    fetchUserInfo()
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    if (!password.trim()) { setError('Por favor, informe uma senha'); return }
    if (!isPasswordValid(password)) { setError('Senha não atende aos requisitos de segurança'); return }
    if (password !== confirmPassword) { setError('As senhas não correspondem'); return }

    setLoading(true)

    try {
      await api.post('/company-users/first-access-confirm', {
        token,
        password
      })

      setSuccess(true)
      toast.success('Senha criada com sucesso!')

      setTimeout(() => {
        const companyId = userInfo?.company_id
        navigate(companyId ? `/login?company_id=${companyId}` : '/login')
      }, 3000)
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Erro ao criar senha'
      setError(typeof errorMessage === 'string' ? errorMessage : 'Erro ao criar senha')
      setLoading(false)
    }
  }

  if (success) {
    return pageWrapper(
      <>
        <PageHeader />
        <div
          className="rounded-2xl p-8 text-center backdrop-blur-2xl"
          style={{
            backgroundColor: 'rgba(10,28,40,0.85)',
            border: '1px solid rgba(37,198,164,0.2)',
            boxShadow: '0 0 40px rgba(37,198,164,0.08)'
          }}
        >
          <div className="flex justify-center mb-4">
            <div className="rounded-full p-4" style={{ backgroundColor: 'rgba(37,198,164,0.15)' }}>
              <span className="material-icons text-5xl" style={{ color: '#25C6A4' }}>check_circle</span>
            </div>
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Bem-vindo!</h2>
          <p className="text-sm mb-6" style={{ color: 'rgba(255,255,255,0.6)' }}>
            Sua senha foi criada com sucesso. Você será redirecionado para o login em instantes.
          </p>
          <div className="flex justify-center">
            <span className="material-icons animate-spin" style={{ color: '#25C6A4' }}>refresh</span>
          </div>
        </div>
      </>
    )
  }

  return pageWrapper(
    <>
      <PageHeader />

      <div
        className="rounded-2xl p-8 animate-fade-in backdrop-blur-2xl"
        style={{
          backgroundColor: 'rgba(10,28,40,0.85)',
          border: '1px solid rgba(37,198,164,0.2)',
          boxShadow: '0 0 40px rgba(37,198,164,0.08)'
        }}
      >
        <h2 className="text-2xl font-bold text-white mb-6">Crie sua Senha</h2>

        {loadingInfo ? (
          <div className="mb-6 text-center">
            <span className="material-icons animate-spin" style={{ color: '#25C6A4' }}>sync</span>
          </div>
        ) : userInfo && !error ? (
          <div className="mb-6 p-4 rounded-lg" style={{ backgroundColor: 'rgba(37,198,164,0.08)', border: '1px solid rgba(37,198,164,0.2)' }}>
            <p className="text-sm text-white font-semibold mb-3">Você está criando senha para:</p>
            <div className="space-y-1 text-sm">
              <p><span style={{ color: 'rgba(255,255,255,0.5)' }}>Empresa:</span> <span className="text-white font-medium ml-2">{userInfo.company_name}</span></p>
              <p><span style={{ color: 'rgba(255,255,255,0.5)' }}>Usuário:</span> <span className="text-white font-medium ml-2">{userInfo.username}</span></p>
              <p><span style={{ color: 'rgba(255,255,255,0.5)' }}>Email:</span> <span className="text-white font-medium ml-2">{userInfo.email}</span></p>
            </div>
          </div>
        ) : null}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <PasswordInput
              label="Crie sua Senha"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Crie uma senha forte"
              disabled={loading}
            />
            <PasswordStrengthMeter password={password} />
          </div>

          <div>
            <PasswordInput
              label="Confirme sua Senha"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirme sua senha"
              disabled={loading}
              error={confirmPassword && password !== confirmPassword ? 'As senhas não correspondem' : ''}
            />
          </div>

          {error && (
            <div className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.4)' }}>
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !isPasswordValid(password) || password !== confirmPassword}
            className="w-full flex items-center justify-center gap-2 py-3 text-white font-medium rounded-lg transition-all duration-200 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            style={{ background: 'linear-gradient(135deg, #25C6A4, #1aa085)', boxShadow: '0 4px 20px rgba(37,198,164,0.3)' }}
            onMouseEnter={e => { if (!loading) e.currentTarget.style.boxShadow = '0 4px 28px rgba(37,198,164,0.5)' }}
            onMouseLeave={e => e.currentTarget.style.boxShadow = '0 4px 20px rgba(37,198,164,0.3)'}
          >
            {loading
              ? <span className="material-icons animate-spin" style={{ fontSize: '18px' }}>sync</span>
              : <span className="material-icons" style={{ fontSize: '18px' }}>person_add</span>
            }
            Criar Senha
          </button>
        </form>

        <p className="text-center text-xs mt-6" style={{ color: 'rgba(255,255,255,0.25)' }}>
          Powered by <span style={{ fontFamily: 'Blare, sans-serif', color: '#FFFFFF' }}>Solid<span style={{ color: '#25C6A4' }}>.</span>Flow</span>
        </p>
      </div>
    </>
  )
}

export default CreatePasswordPage
